import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

/**
 * NexusFAQs
 * Static, curated FAQ section rendered inside the Nexus tab. Content is
 * clinician-facing and intentionally not AI-generated — it explains what
 * Nexus is, when to use it, and how to interpret its output.
 */
const FAQS = [
  {
    q: 'What is Nexus?',
    a: `Nexus is a pre-session preparation tool that helps you anticipate, rehearse, and navigate client resistance. After the "Why" Analysis identifies your client's hidden dynamics and the "Next Steps" Analysis creates their action plan, Nexus answers the most important question in therapy: "What happens when the client goes back to their real life and refuses to change?"

Nexus predicts how your specific client will push back, gives you exact words to handle their resistance, and lets you practice the difficult conversation before the real session.

Example:
You gave a client a micro-experiment: "This week, tell your mother you cannot take her calls after 8 PM."
Nexus predicts: "The client will likely say they forgot, or their mother will guilt-trip them, causing them to abandon the experiment. The client's hidden fear is losing their mother's approval [Point 3]."
Now you walk into the session prepared for exactly this outcome.`,
  },
  {
    q: 'How does Nexus work?',
    a: `Nexus analyzes your client's SQIS data (14 Points), the patterns from the Why Analysis, and the proposed micro-experiments from the Next Steps Analysis. It then generates four things:

• The Objection Matrix — Predicts the exact excuses your client will make.
• Systemic Friction Scripts — Predicts how the client's family/culture will push back.
• The Shame-Free Debriefing Guide — What to say when the client "fails" at their experiment.
• The Live Simulator — An AI roleplay where you practice talking to the client.

Example:
Your client is a 28-year-old man who intellectualizes emotions [Point 6] and avoids conflict [Point 9]. You assigned him a "micro-experiment" to express one need to his partner this week.
Nexus predicts:
Objection: "I didn't say anything because it wasn't the right time. She was stressed from work."
Hidden Dynamic: He is using his partner's stress as a shield to avoid vulnerability [Point 9].
Your Script: "It makes sense you waited for the 'right time.' But I'm curious — if there had been a perfect time with zero stress, what would you have said?"`,
  },
  {
    q: 'Will my client say EXACTLY what Nexus predicts?',
    a: `No. The chance is 0%.

Your client is a complex human being. They will never follow a script. They might come in talking about traffic, their dog, or a completely unrelated problem.

However, Nexus does not predict words. It predicts psychological patterns.

Example:
Nexus predicts: "Client will say they were 'too tired' to do the experiment."
Real client says: "My boss kept me late, my car broke down, and I just couldn't think about it."
The words are completely different, but the psychological move is identical: using external chaos to avoid emotional discomfort.

Because you rehearsed with Nexus, you instantly recognize the pattern. You don't get stuck on the story about the car. You gently pivot:
"It sounds like everything in your environment was screaming at you this week. When the dust settled, did any part of you want to try the exercise? Or did the idea of it feel impossible?"

Nexus trains your clinical reflexes, not your memory.`,
  },
  {
    q: 'What is the "Live Simulator" and how do I use it?',
    a: `The Live Simulator turns the AI into a roleplay version of your specific client. It uses their defense mechanisms, speech patterns, cultural context, and emotional triggers from the SQIS data.

How to use it:
1. Read the Nexus Analysis to understand the predicted resistance.
2. Click "Start Roleplay" at the top of the Nexus tab.
3. A chat window opens. Type what you would say to the client in session.
4. The AI responds in character as the client — with their specific stubbornness, guilt, or defensiveness.
5. Practice your responses until you feel confident.

Example Session:
You (typing): "So, we talked about setting a boundary with your brother this week. How did that go?"
AI (as client): "Honestly? I didn't do it. And I don't think you understand — in our family, you don't just tell your older brother 'no.' It's disrespectful. I can't just act like a Western individualist."
You (practicing): "You're absolutely right — I don't want to ask you to disrespect your family or your values. Let's look at this differently. Is there a way to honor your brother's role while still protecting your own peace?"

You can practice this conversation 5, 10, or 20 times with zero risk. When the real client says something similar, you won't freeze.`,
  },
  {
    q: 'When should I use Nexus?',
    a: `Nexus is most powerful in these specific situations:
• The "Stuck" Client: The client who agrees to everything in session but never changes between sessions.
• High-Resistance Clients: Clients who intellectualize, deflect, get angry, or shut down.
• Culturally Complex Cases: When the intervention might clash with the client's family norms, religious values, or community expectations.
• Pre-Session Anxiety: You have a session with a difficult client in 30 minutes and want to mentally rehearse.
• New Therapists / Interns: You want to practice handling pushback in a safe environment before facing a real client.

Example:
You have a 4:00 PM session with a client who has missed their "homework" three weeks in a row. You feel frustrated and don't know how to address it without sounding like a nagging parent. Open Nexus 15 minutes before the session. Practice your approach in the Simulator. Walk into the room calm, curious, and prepared.`,
  },
  {
    q: 'When should I NOT use Nexus?',
    a: `Nexus is not for every client or every situation. Skip it when:
• The client is in acute crisis (suicidal ideation, active abuse, psychosis). Focus on stabilization and safety planning, not behavioral experiments.
• The client is highly motivated and compliant. If they do their homework without friction, there's no resistance to rehearse.
• You are doing purely unstructured talk therapy. If your modality doesn't involve "experiments," "homework," or "action steps," Nexus won't apply.
• You just want to listen. Sometimes the client just needs a witness. Not every session requires strategy.

Example:
A client calls you in tears because their parent just died. Do NOT open Nexus. Do NOT predict their "objections." Just be present. Nexus is a strategy tool, not a crisis intervention tool.`,
  },
  {
    q: 'How do I use the scripts? Should I read them word-for-word?',
    a: `No. Never read scripts verbatim.

The scripts are training wheels. They show you the direction of the conversation, the tone to use, and the psychological principle behind the words. Adapt them to your natural voice, your therapeutic style, and the moment.

Example:
Nexus Script: "It sounds like your energy was completely drained by the family dynamic this week. When you think about the exercise, what feels heaviest about it?"
Your Natural Version: "Yeah, it sounds like this week just wiped you out. And now the idea of doing the exercise feels like... what? Heavy? Pointless?"

Same principle. Different words. Use the script as a compass, not a map.`,
  },
  {
    q: 'What is the "Righting Reflex Trap" and why does Nexus warn me about it?',
    a: `The "Righting Reflex" is a term from Motivational Interviewing. It's the therapist's instinct to "fix" the client by arguing, persuading, or lecturing them into changing.

When a client says "I didn't do it," the Righting Reflex makes us say:
"But we agreed this was important. Remember why you wanted to change?"

This almost always makes the client more defensive.

Nexus warns you about this trap and gives you an alternative.

Example:
Client says: "I didn't call my friend back. I just don't want to talk to anyone."
Righting Reflex Trap (Don't say this): "But isolation is exactly the pattern we're trying to break. You know calling your friend helps."
Nexus Script (Say this instead): "Part of you wanted to reach out, and part of you really needed the silence. Can we just sit with that tension for a moment? What did the silence give you that the phone call couldn't?"`,
  },
  {
    q: 'What happens if the client "fails" at their experiment?',
    a: `This is one of the most common and painful moments in therapy. The client feels shame. The therapist feels stuck.

Nexus provides a Shame-Free Debriefing Guide that reframes "failure" as "data."

Example:
Client says (looking down, voice quiet): "I didn't do it. I know I said I would. I'm sorry."
Nexus Re-Frame: "You don't need to apologize. We didn't fail this week. We successfully ran an experiment and discovered that the fear of disappointing your mother is currently stronger than the desire to set the boundary. That is incredibly valuable information for us. Now we know exactly where the wall is."

The client's shame dissolves. Curiosity replaces guilt. You adjust the experiment to be even smaller next week.`,
  },
  {
    q: 'Is Nexus a replacement for clinical supervision?',
    a: `No.

Nexus is a preparation tool, like a flight simulator. It helps you rehearse, anticipate, and reflect. But it cannot replace the nuance, ethics, and relational depth of working with a human supervisor.

Use Nexus to:
• Prepare for a specific difficult session.
• Practice your wording.
• Identify your own blind spots.

Use a human supervisor to:
• Process countertransference.
• Navigate ethical dilemmas.
• Get feedback on your overall clinical development.

Think of it this way: Nexus is the 15-minute warm-up before the game. Your supervisor is the coach who watches your career over years.`,
  },
  {
    q: 'Can I use the Simulator with a real client in the room?',
    a: `No. Do not show the Simulator to a client or use it during a live session.

The Simulator is designed for therapist preparation only. Showing a client an AI pretending to be them would feel violating, creepy, and would destroy the therapeutic alliance.

The Simulator is your private rehearsal space. What you bring into the session is not the AI's words — it is your own grounded, prepared, and confident presence.`,
  },
  {
    q: 'What if Nexus predicts something that feels "wrong" for my client?',
    a: `Trust your clinical judgment over the AI.

Nexus generates rehearsal hypotheses based on patterns in the SQIS data. It does not know what a client will say. If a simulated scenario feels off, ignore it. If a script feels culturally insensitive or misaligned with your client's personality, rewrite it.

Example:
Nexus predicts: "The client will resist the experiment because of fear of abandonment [Point 7]."
But you know from 6 months of working with this client that their resistance is actually about perfectionism, not abandonment.

Use your knowledge. Adjust the script. Nexus is a starting point, not a diagnosis.`,
  },
  {
    q: 'How often should I use Nexus for the same client?',
    a: `You do not need to run Nexus for every session. Use it strategically:
• Before a high-stakes session (e.g., addressing a rupture, introducing a difficult topic).
• When a client has "failed" multiple experiments and you need a fresh approach.
• When you feel stuck, frustrated, or anxious about an upcoming session.
• When the client's life circumstances change (new job, family death, relationship change) and old patterns may shift.

Example:
Your client has been doing well for 8 weeks. Suddenly, they tell you they are moving back in with their parents. The old family dynamics will reactivate. Run a fresh Nexus Analysis to anticipate the new systemic friction before the next session.`,
  },
  {
    q: 'Is the AI actually "thinking" like my client?',
    a: `No. The AI does not have consciousness, emotions, or genuine understanding.

What it does is highly sophisticated pattern recognition. It reads your client's SQIS data, identifies their defense mechanisms, speech patterns, and cultural context, and generates the most statistically likely responses based on those patterns.

Think of it as a very well-researched improv actor. It's not "feeling" your client's pain. But it can convincingly simulate how your client might deflect, intellectualize, or shut down — which is exactly what you need to practice against.`,
  },
  {
    q: "What if I'm an experienced therapist? Is this still useful?",
    a: `Yes — but differently.

If you have 20 years of experience, you don't need Nexus to teach you empathy or basic MI skills. But you can use it as:
• A mirror for blind spots. Even experts have countertransference. Nexus might highlight a cultural friction point you overlooked.
• A time-saver. Instead of spending 20 minutes mentally rehearsing before a tough session, spend 5 minutes in the Simulator.
• A documentation tool. The Objection Matrix and Friction Scripts can be referenced in your session notes as part of your treatment planning.

Example:
You've been seeing a client for 2 years. You know them deeply. But their cultural context is different from yours. Nexus flags: "In this client's culture, directly challenging an elder is considered deeply shameful. The client may agree to the boundary-setting experiment in session but feel intense cultural guilt attempting it at home."

You pause. You realize you hadn't fully considered that angle. You adjust your approach. Even experts have blind spots.`,
  },
];

const NexusFAQs = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx((cur) => (cur === i ? null : i));

  return (
    <div className="mt-2" data-testid="nexus-faqs-section">
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="w-5 h-5 text-emerald-700" />
        <h3 className="text-lg font-semibold text-gray-900" data-testid="nexus-faqs-title">
          Nexus — Frequently Asked Questions
        </h3>
      </div>
      <p className="text-sm text-gray-600 mb-4 italic">
        A clinician-facing guide to what Nexus is, when to use it, and how to interpret its output.
        Tap a question to expand.
      </p>
      <div className="space-y-2">
        {FAQS.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <div
              key={i}
              className={`border rounded-lg transition-colors ${
                isOpen ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 bg-white'
              }`}
              data-testid={`nexus-faq-item-${i}`}
            >
              <button
                type="button"
                onClick={() => toggle(i)}
                className="w-full flex items-start justify-between gap-3 text-left px-4 py-3 hover:bg-emerald-50/60 rounded-lg"
                aria-expanded={isOpen}
                data-testid={`nexus-faq-toggle-${i}`}
              >
                <span className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-gray-900 pt-0.5">{item.q}</span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 flex-shrink-0 mt-1 transition-transform ${
                    isOpen ? 'rotate-180 text-emerald-700' : ''
                  }`}
                />
              </button>
              {isOpen && (
                <div
                  className="px-4 pb-4 pl-14 text-sm text-gray-700 whitespace-pre-line leading-relaxed"
                  data-testid={`nexus-faq-answer-${i}`}
                >
                  {item.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-6 text-xs italic text-gray-500" data-testid="nexus-faqs-disclaimer">
        This section presents hypotheses for exploration based on patterns in the client&apos;s own
        responses, triangulated across the structured exploration phases. This is not a diagnostic
        or conclusive interpretation, and is intended to support — not replace — clinical judgment.
      </p>
    </div>
  );
};

export default NexusFAQs;
